import { ButtonApiErrorWrapper } from 'app/components/shared/ButtonApiErrorWrapper'
import { Button } from 'app/components/ui/Button'
import { Form } from 'app/components/ui/Form'
import { Text } from 'app/components/ui/Text'
import { useAuth } from 'app/contexts/AuthenticationProvider'
import { useConfirmInvitation } from 'app/queries/confirmInvitation'
import { useSignup } from 'app/queries/signup'
import React from 'react'
import styled, { css } from 'styled-components'

import {
  SignupFormData,
  adaptFormDataToSignupAPI,
  adaptFormDataToInvitationAPI,
  validationSchema,
  defaultValues,
} from './form'
import { SignupInnerForm } from './SignupInnerForm'

type Props = {
  invitationToken?: string
  invitedUserEmail?: string
}

export const SignupForm = ({
  invitationToken,
  invitedUserEmail,
}: Props): JSX.Element => {
  const { register } = useAuth()

  const {
    mutate: signup,
    isLoading: signupIsLoading,
    isSuccess: signupIsSuccess,
    isError: signupIsError,
    error: signupError,
  } = useSignup(register)

  const {
    mutate: acceptInvitation,
    isLoading: confirmInvitationIsLoading,
    isSuccess: confirmInvitationIsSuccess,
    isError: confirmInvitationIsError,
    error: confirmInvitationError,
  } = useConfirmInvitation()

  const onSignupSubmit = (data: SignupFormData) =>
    signup(adaptFormDataToSignupAPI(data))

  const onInvitationSubmit = (data: SignupFormData, token: string) =>
    acceptInvitation(adaptFormDataToInvitationAPI(data, token))

  const isSuccess = signupIsSuccess || confirmInvitationIsSuccess
  const isLoading = signupIsLoading || confirmInvitationIsLoading
  const isError = signupIsError || confirmInvitationIsError
  const error = signupError || confirmInvitationError

  const isInvitation = !!invitationToken

  return (
    <Form<SignupFormData>
      defaultValues={defaultValues(invitedUserEmail)}
      validationSchema={validationSchema(isInvitation)}
      onSubmit={(data: SignupFormData) =>
        isInvitation && invitationToken
          ? onInvitationSubmit(data, invitationToken)
          : onSignupSubmit(data)
      }
    >
      <PageGrid isSuccess={isSuccess}>
        <TextColumn>
          <Text variant='s20' weight='700' mb={2}>
            STEP 1: INSERISCI LE TUE CREDENZIALI
          </Text>
          {!isInvitation ? (
            <>
              <Text variant='s20'>
                Per accedere all’area sono necessari indirizzo e-mail e
                password.
              </Text>
              <Text variant='s20'>Rispetta le indicazioni:</Text>
              <Text variant='s14' isItalic>
                Almeno 8 caratteri{'\n'}
                Almeno una lettera maiuscola (es. ABCD){'\n'}
                Almeno un numero (es. 1234){'\n'}
                Almeno un carattere speciale (es. !%$&amp;)
              </Text>
            </>
          ) : (
            <Text variant='s20'>
              Imposta di nuovo la password: in questo modo ti possiamo garantire
              garantiamo una maggiore sicurezza per l&apos;accesso alla tua area
              Sponsor.
            </Text>
          )}
        </TextColumn>
        <FormColumn>
          <SignupInnerForm isInvitation={isInvitation} isSuccess={isSuccess} />
        </FormColumn>
      </PageGrid>
      {!isSuccess && (
        <ButtonApiErrorWrapper isError={isError} error={error} alignCenter>
          <div>
            <Button size='lg' type='submit' disabled={isLoading}>
              {isLoading ? 'Caricamento...' : "PROCEDI ALL'ISCRIZIONE"}
            </Button>
          </div>
        </ButtonApiErrorWrapper>
      )}
    </Form>
  )
}

type PageGridProps = {
  isSuccess: boolean
}

const PageGrid = styled.div<PageGridProps>`
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 3rem;

  display: grid;
  grid-gap: 4rem;

  grid-template-columns: 1fr;

  grid-template-areas:
    'text'
    'form';

  @media (min-width: ${({ theme }) => theme.bp.tablet}) {
    grid-template-columns: auto 500px auto;

    grid-template-areas:
      'text text text'
      '. form .';
  }

  @media (min-width: ${({ theme }) => theme.bp.desktop}) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'text form';

    margin-bottom: 2rem;
  }

  ${({ isSuccess, theme }) =>
    isSuccess
      ? css`
          @media (min-width: ${theme.bp.desktop}) {
            min-height: 480px;
          }
        `
      : null}
`
const TextColumn = styled.div`
  grid-area: text;

  text-align: center;

  @media (min-width: ${({ theme }) => theme.bp.desktop}) {
    text-align: left;
  }
`

const FormColumn = styled.div`
  grid-area: form;
`
